/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { RefObject } from 'preact';
import { LegendItem, LegendSection } from './Legend.type';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';

/**
 * Checks if two layout infos are equal.
 */
const areEqualLayoutInfo = function (info1: LayoutInfo[], info2: LayoutInfo[]) {
  return (
    info1.length === info2.length &&
    info1.every(
      (value, index) =>
        value.isWrapped === info2[index].isWrapped &&
        value.maxItemWidth === info2[index].maxItemWidth &&
        info1[index].itemsPerRow === info2[index].itemsPerRow
    )
  );
};

type Props<K> = {
  items: LegendItem<K>[] | LegendSection<K>[];
  rootRef: RefObject<HTMLDivElement>;
  rootDimsRef: RefObject<DOMRect | undefined>;
  isSectional?: boolean;
  orientation?: 'vertical' | 'horizontal';
};

export type LayoutInfo = {
  isWrapped: boolean;
  maxItemWidth: number;
  itemsPerRow: number;
};

/**
 * Returns maximum of the width among the items section of legend.
 * @param legend BaseLegend element
 * @returns
 */
const getMaxWidth = (baseLegend: HTMLElement) => {
  let maxItemWidth = 0;
  [...baseLegend.children].forEach((e) => {
    const item = e as HTMLElement;
    maxItemWidth = Math.max(maxItemWidth, Math.ceil(item.getBoundingClientRect().width));
  });

  return maxItemWidth;
};

export function useLegendLayout<K>({
  items,
  rootRef,
  rootDimsRef,
  isSectional = false,
  orientation
}: Props<K>) {
  const [layoutInfo, setLayoutInfo] = useState<LayoutInfo[]>([]);
  const isColumnWidthValidRef = useRef(false);
  const previousOrientationRef = useRef(orientation);
  useLayoutEffect(() => {
    if (!rootRef.current) {
      return;
    }
    // recalculate rootDims everytime items change.
    rootDimsRef.current = rootRef.current.getBoundingClientRect();
    previousOrientationRef.current = orientation;
    const legendLayoutInfo: LayoutInfo[] = [];
    const rootElement = isSectional ? rootRef.current.children[0] : rootRef.current;
    [...rootElement.children].forEach((section: Element) => {
      const sectionWidth = (section as HTMLElement).getBoundingClientRect().width;
      const baseLegend = isSectional ? section.children[section.childElementCount - 1] : section;
      let isWrapped = sectionWidth > rootDimsRef.current?.width!;
      if (baseLegend.childElementCount > 0 && sectionWidth === rootDimsRef.current?.width!) {
        // If text is trucated, sectionwidth and rootWidth are equal but legend section could still be wrapped.
        // compare offsetTop with last item to determine wrapping.
        isWrapped =
          (baseLegend.children[baseLegend.childElementCount - 1] as HTMLElement).offsetTop >
          (section as HTMLElement).offsetTop;
      }
      const maxItemWidth = getMaxWidth(baseLegend as HTMLElement);
      legendLayoutInfo.push({
        maxItemWidth: Math.min(maxItemWidth, rootDimsRef.current?.width!),
        itemsPerRow: Math.max(1, Math.floor(rootDimsRef.current?.width! / maxItemWidth)),
        isWrapped
      });
    });

    if (!areEqualLayoutInfo(layoutInfo, legendLayoutInfo)) {
      isColumnWidthValidRef.current = true;
      setLayoutInfo(legendLayoutInfo);
    }
  }, [items, orientation]);

  return {
    layoutInfo: previousOrientationRef.current != orientation ? [] : layoutInfo,
    isColumnWidthValidRef
  };
}
